/**
 * Teintes dérivées d'une couleur de statut (design system Argalis).
 *
 * Extrait de `StatusChip` pour devenir partageable : dès qu'un écran RÉPÈTE
 * l'information d'un statut hors de sa chip (le motif de perte dans le bandeau
 * de synthèse, par exemple), il doit reprendre EXACTEMENT la même encre, sinon
 * deux rouges légèrement différents cohabitent à l'écran (retour PO 2026-07-28).
 *
 * Toutes les teintes sont calculées en `color-mix` contre les tokens du thème :
 * elles restent lisibles en clair comme en sombre, sans table de correspondance.
 * Le fichier ne porte AUCUN composant — un module de composant ne peut pas
 * exporter de fonction sans casser le fast refresh (`only-export-components`).
 */

import type { CSSProperties } from 'react'

/** Encre du libellé : la couleur du statut, encrée pour rester lisible. */
export function statusInkColor(color: string): string {
    return `color-mix(in srgb, ${color} 76%, hsl(var(--foreground)))`
}

/** Fond de la pill : teinte douce de la couleur du statut. */
export function statusTintColor(color: string): string {
    return `color-mix(in srgb, ${color} 16%, hsl(var(--background)))`
}

/**
 * Pill pleine theme-aware : fond teinté doux + texte encré lisible.
 *
 * 🔴 **`encre` découple la SURFACE de l'ENCRE, et c'est une exigence de
 * contraste** (constat PO du 2026-09-08 : *« attention aux contrastes »*).
 *
 * Avec une seule couleur, les deux tirent en sens INVERSE : plus elle est vive,
 * plus le fond est agréable et plus l'encre devient claire. Mesuré sur les
 * jetons du dépôt, pour l'état « Opérationnel » :
 *
 * | Couleur passée | Fond | Encre | Contraste |
 * |---|---|---|---|
 * | `--success` (teal sombre) | pâle grisé | `#0D655B` | **5,5:1** ✓ mais terne |
 * | `--success-chart` (vif) | menthe vive | `#569D8B` | **2,9:1** ✗ échoue AA |
 * | `-chart` + encre `--success` | menthe vive | `#0D655B` | **6,1:1** ✓ et vif |
 *
 * La troisième ligne est la seule qui tienne les deux. `encre` est FACULTATIF :
 * les appelants d'origine (devis, appels d'offres) gardent exactement le rendu
 * qu'ils avaient — un référentiel de statuts y porte un hexadécimal choisi par
 * l'organisme, et on ne peut pas lui deviner une encre.
 */
export function pillStyle(color: string, encre?: string): CSSProperties {
    return {
        backgroundColor: statusTintColor(color),
        color: encre ? statusInkColor(encre) : statusInkColor(color),
    }
}

/** Couleur du liseré de sélection (version encrée, toujours visible). */
export function ringColor(color: string): string {
    return `color-mix(in srgb, ${color} 50%, hsl(var(--foreground)))`
}
